import { useState, useEffect } from 'react';
import { Clock, Wrench, FileText, CheckCircle, AlertTriangle, Camera, DollarSign } from 'lucide-react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { supabase } from '../../lib/supabase';
import { date } from '../../utils/formatters';

const EVENT_ICONS = {
  checkin_completed: Camera,
  quote_created: FileText,
  quote_sent: FileText,
  quote_decision: CheckCircle,
  status_change: Wrench,
  service_completed: CheckCircle,
  payment_received: DollarSign,
  note_added: FileText,
  alert: AlertTriangle,
};

const EVENT_COLORS = {
  checkin_completed: 'bg-blue-500',
  quote_created: 'bg-purple-500',
  quote_sent: 'bg-purple-500',
  quote_decision: 'bg-green-500',
  status_change: 'bg-yellow-500',
  service_completed: 'bg-green-500',
  payment_received: 'bg-emerald-500',
  note_added: 'bg-gray-500',
  alert: 'bg-red-500',
};

export default function VehicleTimeline({ veiculoId, clienteId, limit = 20 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadTimeline = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_order_events')
        .select(`
          *,
          ordens_servico!inner(numero, data_entrada, status)
        `)
        .eq('ordens_servico.veiculo_id', veiculoId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Erro ao carregar timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
  }, [veiculoId, clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Card>
        <div className="p-4 text-center text-zinc-400">Carregando timeline...</div>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <div className="p-4 text-center text-zinc-400">Nenhum evento registrado</div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Clock size={20} />
          Timeline do Veículo
        </h3>
        <p className="text-sm text-zinc-400">Histórico completo de eventos</p>
      </div>

      <div className="space-y-4">
        {events.map((event, index) => {
          const Icon = EVENT_ICONS[event.event_type] || FileText;
          const color = EVENT_COLORS[event.event_type] || 'bg-gray-500';
          
          return (
            <div key={event.id} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${color} text-white`}>
                  <Icon size={18} />
                </div>
                {index < events.length - 1 && (
                  <div className="w-0.5 flex-1 bg-white/10" />
                )}
              </div>

              <div className="flex-1 pb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{event.title}</p>
                    {event.description && (
                      <p className="mt-1 text-sm text-zinc-400">{event.description}</p>
                    )}
                  </div>
                  <span className="text-xs text-zinc-500">{date(event.created_at)}</span>
                </div>

                {event.ordens_servico && (
                  <div className="mt-2">
                    <Badge variant="outline" className="text-xs">
                      OS #{event.ordens_servico.numero}
                    </Badge>
                  </div>
                )}

                {event.metadata && Object.keys(event.metadata).length > 0 && (
                  <div className="mt-2 rounded-lg bg-surface2 p-2 text-xs text-zinc-400">
                    {Object.entries(event.metadata).map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="font-medium">{key}:</span>
                        <span>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
